import { useEffect, ReactNode, createContext, useContext } from "react";
import useIsMobile from "../../customHooks/useIsMobile";

interface SheetContextType {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
}

const SheetContext = createContext<SheetContextType | null>(null);

const useSheetContext = () => {
  const context = useContext(SheetContext);
  if (!context) {
    throw new Error('Sheet compound components must be used within Sheet component');
  }
  return context;
};

interface SheetProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  className?: string;
}

interface SheetSubComponentProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const Sheet = ({ children, isOpen, onClose, className }: SheetProps) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    const handleEnterKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isOpen) {
        event.preventDefault();
      }
    };

    window.addEventListener('keydown', handleEnterKey);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keydown', handleEnterKey);
    };
  }, [isOpen, onClose]);

  const containerClasses = `
    fixed top-0 left-0 h-[100dvh] w-screen
    transition-all ease-in-out duration-300
    ${isOpen ? 'bg-black/50 z-[1000]' : 'bg-transparent pointer-events-none -z-10'}
    ${isMobile ? '' : 'flex items-center justify-center'}
  `;

  const contentClasses = `
    flex flex-col justify-between items-center bg-white 
    overflow-hidden transition-all duration-300 ease-in-out
    ${isMobile 
      ? 'rounded-t-[30px] w-full absolute bottom-0 max-h-[85dvh]' 
      : 'rounded-[6px] rounded-tl-[30px] rounded-br-[30px] max-w-[500px] w-full max-h-[85dvh]'}
  `;

  const transformStyle = {
    transform: isMobile 
      ? (isOpen ? 'translateY(0)' : 'translateY(100%)') 
      : (isOpen ? 'scale(1)' : 'scale(0.9)'),
    opacity: !isMobile ? (isOpen ? 1 : 0) : undefined
  };

  return (
    <SheetContext.Provider value={{ isOpen, onClose, isMobile }}>
      <div id="sheet-container" className={`${containerClasses} ${className}`} aria-modal={isOpen} role="dialog">
        <div id="sheet-overlay" className="absolute top-0 left-0 w-full h-full" onClick={onClose} aria-hidden="true" />
        
        <div id="sheet-content" className={contentClasses} style={transformStyle}>
          {isMobile && (
            <div id="sheet-handle-wrapper" className="flex items-center justify-center pt-3 pb-4 w-full" onClick={onClose}>
              <div
                id="sheet-handle"
                className="cursor-pointer bg-[#EDEDED] rounded-[10px] w-1/2 h-[5px]"
                aria-label="Close sheet"
              />
            </div>
          )}

          <div id="sheet-body-container" className="w-full flex-1 overflow-hidden flex flex-col">
            {children}
          </div>

          <style>
            {`.custom-padding-right::-webkit-scrollbar { background-color: transparent; }`}
          </style>
        </div>
      </div>
    </SheetContext.Provider>
  );
};

const Header = ({ children, className = "", style }: SheetSubComponentProps) => {
  return (
    <div 
      id="sheet-header" 
      className={`w-full ${className}`}
      style={style}
    >
      {children}
    </div>
  );
};

const Body = ({ children, className = "", style }: SheetSubComponentProps) => {
  return (
    <div 
      id="sheet-body" 
      className={`flex-1 overflow-y-auto w-full ${className}`}
      style={style}
    >
      {children}
    </div>
  );
};

const Footer = ({ children, className = "", style }: SheetSubComponentProps) => {
  return (
    <div 
      id="sheet-footer" 
      className={`w-full ${className}`}
      style={style}
    >
      {children}
    </div>
  );
};

// Attach subcomponents to main component
Sheet.Header = Header;
Sheet.Body = Body;
Sheet.Footer = Footer;

export { Sheet, useSheetContext };
